import type { Entity, MutationKind } from './components'
import { T } from './tunables'

export interface MutationDef {
  kind: MutationKind
  /** Placeholder name — final copy authored via amoeba-voice. */
  name: string
  /** Placeholder flavor — final copy authored via amoeba-voice. */
  flavor: string
  /** Tint for the orb SDF and HUD chip background. */
  tint: [number, number, number]
  /** HUD chip border color signal. */
  sign: 'buff' | 'curse' | 'mixed' | 'delight'
}

// Stack cap — lowered from 5 to 4 after game-design pass (cap-5 numbers were
// degenerate at the high end for several mutations; cap-4 keeps the gradient
// without the cliff). Numbers tuned down from initial draft after the
// amoeba-game-design review pass.

export const MUTATION_DEFS: Record<MutationKind, MutationDef> = {
  bouquet:           { kind: 'bouquet',          name: 'Bouquet',         flavor: 'Body now broadcasts to nearby cultures, hostile and otherwise.', tint: [1.0,  0.7,  0.85], sign: 'mixed'   },
  'static-cling':    { kind: 'static-cling',     name: 'Static cling',    flavor: 'Loose nutrients in the medium drift toward the membrane.',     tint: [0.7,  0.95, 1.0],  sign: 'buff'    },
  'nucleus-envy':    { kind: 'nucleus-envy',     name: 'Nucleus envy',    flavor: 'DNA absorption up. Maximum integrity down. Net: greed.',       tint: [0.95, 0.6,  0.4],  sign: 'mixed'   },
  'wet-socks':       { kind: 'wet-socks',        name: 'Wet socks',       flavor: 'Mobility reduced. The cause is not under further investigation.', tint: [0.4,  0.55, 0.7],  sign: 'curse'   },
  'borrowed-teeth':  { kind: 'borrowed-teeth',   name: 'Borrowed teeth',  flavor: 'Allies bite harder. Origin of the teeth is unspecified.',      tint: [0.95, 0.95, 0.85], sign: 'buff'    },
  'eyes-everywhere': { kind: 'eyes-everywhere',  name: 'Eyes everywhere', flavor: 'Additional ocelli with no apparent function. Decorative.',     tint: [0.8,  0.4,  0.95], sign: 'delight' },
}

export const ALL_MUTATION_KINDS: MutationKind[] = Object.keys(MUTATION_DEFS) as MutationKind[]

export function emptyMutations(): NonNullable<Entity['mutations']> {
  return { stacks: {}, seenKinds: new Set() }
}

function stackOf(player: Entity, kind: MutationKind): number {
  return player.mutations?.stacks[kind] ?? 0
}

// Apply a single pickup of `kind`. Returns whether the stack actually applied
// and whether this is the first time this kind has been picked up this run.
export function applyMutationStack(player: Entity, kind: MutationKind): {
  applied: boolean
  isFirstThisRun: boolean
} {
  player.mutations ??= emptyMutations()
  const m = player.mutations
  const cur = m.stacks[kind] ?? 0
  if (cur >= T.mutations.maxStacks) {
    // Orb is consumed but no further effect; flag the pickup so HUD pulses still play.
    m.recentPickup = { kind, flashTimer: 0.6 }
    return { applied: false, isFirstThisRun: false }
  }
  m.stacks[kind] = cur + 1
  m.recentPickup = { kind, flashTimer: 0.6 }
  const isFirst = !m.seenKinds.has(kind)
  m.seenKinds.add(kind)

  if (kind === 'eyes-everywhere') pushEyeSockets(player, T.mutations.eyesPerStack)
  // Stat effects (speed, max HP, …) land via recomputeMods on the next frame's
  // mods pass — importing it here would cycle (mods.ts reads our accessors).

  return { applied: true, isFirstThisRun: isFirst }
}

// --- Stat accessors ---------------------------------------------------------
// Each returns identity (1.0× / 0) when no relevant stack is present.

export function mutationSpeedMult(p: Entity): number {
  return Math.pow(T.mutations.wetSocks.speedMult, stackOf(p, 'wet-socks'))
}

export function mutationDnaMult(p: Entity): number {
  return Math.pow(T.mutations.nucleusEnvy.dnaMult, stackOf(p, 'nucleus-envy'))
}

export function mutationMaxHpMult(p: Entity): number {
  return Math.pow(T.mutations.nucleusEnvy.maxHpMult, stackOf(p, 'nucleus-envy'))
}

export function mutationOrbMagnetRange(p: Entity): number {
  return T.mutations.staticCling.magnetRange * stackOf(p, 'static-cling')
}

export function mutationAllyDamageMult(p: Entity): number {
  return Math.pow(T.mutations.borrowedTeeth.allyDamageMult, stackOf(p, 'borrowed-teeth'))
}

export function mutationRecruitReachAdd(p: Entity): number {
  return T.mutations.bouquet.recruitReachPerStack * stackOf(p, 'bouquet')
}

export function mutationDetectionRadiusMult(p: Entity): number {
  return Math.pow(T.mutations.bouquet.detectionMult, stackOf(p, 'bouquet'))
}

// --- Helpers ---------------------------------------------------------------

// Golden angle — successive ocelli land in the widest remaining gap, so the
// decorative extra eyes read as an even, deliberate ring rather than a random
// scatter, no matter how many stacks the player takes.
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5))

function pushEyeSockets(player: Entity, count: number): void {
  if (!player.eyes) return
  const remaining = Math.max(0, T.mutations.eyesTotalCap - player.eyes.sockets.length)
  const n = Math.min(count, remaining)
  for (let i = 0; i < n; i++) {
    // Index among the *extra* ocelli (the base 2 googly eyes come first), so
    // the golden-angle fan is continuous across separate pickups.
    const ocellus = player.eyes.sockets.length - 2
    // Start near the top and fan by the golden angle on a uniform ring.
    const angle = -Math.PI / 2 + ocellus * GOLDEN_ANGLE
    const r = 0.62
    player.eyes.sockets.push({
      offset: { x: Math.cos(angle) * r, y: Math.sin(angle) * r },
      radius: 0.16, // uniform small ocelli — secondary to the main eyes
    })
    player.eyes.pupilPos.push({ x: 0, y: 0 })
    player.eyes.pupilVel.push({ x: 0, y: 0 })
  }
}

export function pickRandomMutationKind(): MutationKind {
  return ALL_MUTATION_KINDS[Math.floor(Math.random() * ALL_MUTATION_KINDS.length)]
}
